import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';
import { API } from '../../lib/api';
import { ShieldCheck, ShieldOff, Loader2, Copy, CheckCircle2 } from 'lucide-react';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';

export default function MfaSection() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  // enrollment flow
  const [setup, setSetup] = useState(null); // { secret, otpauth_uri }
  const [code, setCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState(null);
  // disable flow
  const [disableCode, setDisableCode] = useState('');
  const [showDisable, setShowDisable] = useState(false);

  const loadStatus = async () => {
    try {
      const r = await axios.get(`${API}/auth/mfa/status`, { withCredentials: true });
      setStatus(r.data);
    } catch {
      setStatus(null);
    }
  };

  useEffect(() => { loadStatus(); }, []);

  if (!status || !status.eligible) return null;

  const startSetup = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/auth/mfa/setup`, {}, { withCredentials: true });
      setSetup(r.data);
      setCode('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to start MFA setup');
    } finally {
      setBusy(false);
    }
  };

  const confirmEnable = async () => {
    if (code.trim().length < 6) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/auth/mfa/enable`, { code: code.trim() }, { withCredentials: true });
      setRecoveryCodes(r.data.recovery_codes || []);
      setSetup(null);
      toast.success('Two-factor authentication enabled');
      await loadStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Invalid code');
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    if (disableCode.trim().length < 6) return;
    setBusy(true);
    try {
      await axios.post(`${API}/auth/mfa/disable`, { code: disableCode.trim() }, { withCredentials: true });
      toast.success('Two-factor authentication disabled');
      setShowDisable(false);
      setDisableCode('');
      setRecoveryCodes(null);
      await loadStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Invalid code');
    } finally {
      setBusy(false);
    }
  };

  const copyCodes = () => {
    navigator.clipboard.writeText((recoveryCodes || []).join('\n')).then(
      () => toast.success('Recovery codes copied'),
      () => toast.error('Copy failed — write them down manually'),
    );
  };

  return (
    <Card className="p-6" data-testid="profile-mfa">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
          <ShieldCheck className="h-4 w-4" /> Two-factor authentication (TOTP)
        </h2>
        {status.enabled
          ? <Badge className="bg-emerald-500/15 text-emerald-300 border border-emerald-500/30" data-testid="mfa-badge-enabled">Enabled</Badge>
          : <Badge className="bg-amber-500/15 text-amber-300 border border-amber-500/30" data-testid="mfa-badge-disabled">Not enabled</Badge>}
      </div>

      {recoveryCodes && (
        <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-4 mb-4" data-testid="mfa-recovery-codes">
          <p className="text-sm font-medium text-emerald-300 flex items-center gap-2"><CheckCircle2 className="h-4 w-4" /> Save your recovery codes</p>
          <p className="text-xs text-muted-foreground mt-1 mb-3">
            Each code works once if you lose access to your authenticator. They are shown only now.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 font-mono text-xs">
            {recoveryCodes.map((c) => (
              <span key={c} className="rounded border border-border bg-background px-2 py-1.5 text-center">{c}</span>
            ))}
          </div>
          <Button size="sm" variant="outline" className="mt-3" onClick={copyCodes} data-testid="mfa-copy-codes">
            <Copy className="h-3.5 w-3.5 mr-1.5" /> Copy all
          </Button>
        </div>
      )}

      {status.enabled ? (
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Your account requires a 6-digit authenticator code at sign-in.
            {' '}<span className="text-foreground/80">{status.recovery_codes_remaining}</span> recovery code{status.recovery_codes_remaining === 1 ? '' : 's'} remaining.
          </p>
          {showDisable ? (
            <div className="flex items-center gap-2 max-w-sm">
              <Input value={disableCode} onChange={(e) => setDisableCode(e.target.value)}
                placeholder="Code or recovery code" className="font-mono" data-testid="mfa-disable-input" />
              <Button variant="destructive" size="sm" disabled={busy || disableCode.trim().length < 6}
                onClick={disable} data-testid="mfa-disable-confirm">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Disable'}
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setShowDisable(false)}>Cancel</Button>
            </div>
          ) : (
            <Button variant="outline" size="sm" onClick={() => setShowDisable(true)} data-testid="mfa-disable-start">
              <ShieldOff className="h-3.5 w-3.5 mr-1.5" /> Disable MFA
            </Button>
          )}
        </div>
      ) : setup ? (
        <div className="grid gap-5 sm:grid-cols-[auto_1fr]" data-testid="mfa-setup-panel">
          <div className="rounded-xl bg-white p-3 w-fit h-fit">
            <QRCodeSVG value={setup.otpauth_uri} size={148} data-testid="mfa-qr" />
          </div>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              1. Scan the QR with Google Authenticator, 1Password or Authy.<br />
              2. Or add the key manually: <code className="font-mono text-xs text-foreground/90 bg-muted/50 rounded px-1.5 py-0.5" data-testid="mfa-secret">{setup.secret}</code><br />
              3. Enter the 6-digit code to confirm.
            </p>
            <div className="flex items-center gap-2 max-w-sm">
              <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="123456"
                inputMode="numeric" maxLength={8} className="font-mono tracking-[.3em]" data-testid="mfa-enable-input" />
              <Button size="sm" disabled={busy || code.trim().length < 6} onClick={confirmEnable} data-testid="mfa-enable-confirm">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Confirm'}
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setSetup(null)}>Cancel</Button>
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Protect your admin account with a time-based one-time password. Required for SOC 2 / CIS compliance readiness.
          </p>
          <Button size="sm" onClick={startSetup} disabled={busy} data-testid="mfa-enable-start">
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <ShieldCheck className="h-3.5 w-3.5 mr-1.5" />} Enable MFA
          </Button>
        </div>
      )}
    </Card>
  );
}
